'use client'

import useSWR from 'swr'
import { useEffect, useState } from 'react'
import { useParams, usePathname, useRouter } from 'next/navigation'
import useAxiosInstance from '@/lib/axios'

// When NEXT_PUBLIC_AUTH_MODE=token, use Bearer token auth instead of cookies.
// Set this in .env.local on clients that run on a different host than the API.
const isTokenMode = process.env.NEXT_PUBLIC_AUTH_MODE === 'token'
const AUTH_EVENT_KEY = 'auth_event'

export const useAuth = ({ middleware, redirectIfAuthenticated } = {}) => {
    const router = useRouter()
    const params = useParams()
    const pathname = usePathname()
    const axios = useAxiosInstance()

    const [isSigning, setIsSigning] = useState(false)

    const [permissions, setPermissions] = useState([])
    const setupPath = '/security/setup'

    const base64UrlToBuffer = value => {
        const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
        const padLength = (4 - (normalized.length % 4)) % 4
        const padded = normalized + '='.repeat(padLength)
        const binary = atob(padded)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i += 1) {
            bytes[i] = binary.charCodeAt(i)
        }
        return bytes.buffer
    }

    const bufferToBase64Url = buffer => {
        const bytes = new Uint8Array(buffer)
        let binary = ''
        for (let i = 0; i < bytes.byteLength; i += 1) {
            binary += String.fromCharCode(bytes[i])
        }
        return btoa(binary)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/g, '')
    }

    // CSRF helper for Laravel Sanctum (skipped in token mode)
    const csrf = () =>
        isTokenMode ? Promise.resolve() : axios.get('/sanctum/csrf-cookie')

    const setAuthToken = token => {
        if (token) {
            axios.defaults.headers['Authorization'] = `Bearer ${token}`
        } else {
            delete axios.defaults.headers['Authorization']
        }
    }

    const clearClientAuthState = () => {
        localStorage.removeItem('roles')
        localStorage.removeItem('permissions')
        localStorage.removeItem('auth_token')

        setAuthToken(null)
        delete axios.defaults.headers.common['Authorization']

        try {
            document.cookie = 'logged_in=; path=/; max-age=0; SameSite=Lax'
        } catch (e) {
            // ignore
        }
    }

    const hasLoggedInMarker = () =>
        typeof document !== 'undefined' &&
        document.cookie
            .split(';')
            .map(cookie => cookie.trim())
            .some(cookie => cookie === 'logged_in=1')

    const {
        data: user,
        error,
        isLoading,
        isValidating,
        mutate,
    } = useSWR(
        '/api/user',
        async () => {
            try {
                // In token mode, bail early if no token exists (interceptor won't have anything to attach)
                if (isTokenMode && !localStorage.getItem('auth_token')) {
                    throw { response: { status: 401 } }
                }

                // In cookie mode, fail fast when the frontend marker cookie is missing.
                // This avoids waiting on a slow backend just to discover the session is unauthenticated.
                if (
                    !isTokenMode &&
                    typeof document !== 'undefined' &&
                    !hasLoggedInMarker()
                ) {
                    throw { response: { status: 401 } }
                }

                const res = await axios.get('/api/user')
                return res.data
            } catch (error) {
                if (typeof window !== 'undefined') {
                    // Debug: log response details for failed auth fetches

                    const currentPath = window.location.pathname
                    if (
                        error.response?.status === 401 &&
                        currentPath !== '/login' &&
                        currentPath !== '/verify-email'
                    ) {
                        // Clear the stale logged_in marker cookie so middleware
                        // will redirect on the next navigation.
                        document.cookie =
                            'logged_in=; path=/; max-age=0; SameSite=Lax'
                        router.replace('/login')
                    }
                    if (
                        error.response?.status === 409 &&
                        currentPath !== '/verify-email'
                    ) {
                        router.replace('/verify-email')
                    }
                }
                throw error
            }
        },
        {
            keepPreviousData: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        },
    )

    // Derive roles from user object (works for both cookie and token mode).
    // Falls back to localStorage while user data is still loading (token mode only).
    const roles =
        user?.roles?.map(r => r.name) ??
        (typeof window !== 'undefined'
            ? JSON.parse(localStorage.getItem('roles') || '[]')
            : [])

    // Redirect already-authenticated users away from guest-only pages (e.g. /login → /dashboard)
    useEffect(() => {
        if (user && redirectIfAuthenticated) {
            if (user?.security?.mfa_setup_required && pathname !== setupPath) {
                router.replace(setupPath)
                return
            }
            router.replace(redirectIfAuthenticated)
        }
    }, [user, redirectIfAuthenticated, pathname])

    useEffect(() => {
        if (!user || !user?.security?.mfa_setup_required) {
            return
        }

        if (pathname !== setupPath) {
            router.replace(setupPath)
        }
    }, [user, pathname, router])

    // Fallback guard: enforce cookie-mode auth marker on route changes.
    // This catches cases where cached client state delays a middleware/proxy-style redirect.
    useEffect(() => {
        if (isTokenMode || typeof window === 'undefined') {
            return
        }

        const authPages = new Set([
            '/login',
            '/register',
            '/forgot-password',
            '/verify-email',
        ])
        const isAuthPage =
            authPages.has(pathname) || pathname?.startsWith('/password-reset')

        if (!isAuthPage && !hasLoggedInMarker()) {
            router.replace('/login')
        }
    }, [pathname, router])

    // Keep auth state in sync across browser tabs/windows.
    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }

        const onStorage = event => {
            if (event.key !== AUTH_EVENT_KEY || !event.newValue) {
                return
            }

            try {
                const payload = JSON.parse(event.newValue)
                if (payload?.type === 'logout') {
                    clearClientAuthState()
                    mutate(null, false)
                    router.replace('/login')
                }
            } catch (e) {
                // ignore malformed payloads
            }
        }

        window.addEventListener('storage', onStorage)
        return () => window.removeEventListener('storage', onStorage)
    }, [mutate, router])

    const register = async ({ setErrors, ...props }) => {
        await csrf()
        setErrors([])
        axios
            .post('/register', props)
            .then(data => {
                mutate()
            })
            .catch(error => {
                if (error.response.status !== 422) throw error
                setErrors(error.response.data.errors)
            })
    }

    const login = async ({ setErrors, setStatus, ...props }) => {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('roles')

        await csrf()

        setErrors([])
        setStatus(null)
        setIsSigning(true)

        axios
            .post('/login', props)
            .then(async data => {
                setIsSigning(false)
                let requiresSetup = false
                try {
                    const setupStatus = await axios.get('/api/mfa/setup-status')
                    requiresSetup = !!setupStatus?.data?.mfa_setup_required
                } catch {
                    const refreshedUser = await mutate()
                    requiresSetup =
                        !!refreshedUser?.security?.mfa_setup_required
                }
                mutate().catch(() => {})
                router.replace(requiresSetup ? setupPath : '/')
            })
            .catch(error => {
                setIsSigning(false)
                if (error.response.status !== 422) throw error

                setErrors(error.response.data.errors)
            })
    }

    const tokenBasedlogin = async ({ setErrors, setStatus, ...props }) => {
        setIsSigning(true)
        try {
            await csrf()
            setErrors([])
            setStatus(null)
            let mfaSetupRequired = false

            if (isTokenMode) {
                // --- Token-based auth (cross-origin client) ---
                const response = await axios.post('/login-token', props)
                const {
                    token,
                    roles: userRoles,
                    permissions: userPermissions,
                    mfa_setup_required: needsSetup,
                    security,
                } = response.data
                mfaSetupRequired = !!needsSetup

                // Persist token and profile data
                localStorage.setItem('auth_token', token)
                localStorage.setItem('roles', JSON.stringify(userRoles))
                localStorage.setItem(
                    'permissions',
                    JSON.stringify(userPermissions),
                )
                if (security) {
                    localStorage.setItem('security', JSON.stringify(security))
                }

                // Attach to axios for subsequent requests
                axios.defaults.headers.common['Authorization'] =
                    `Bearer ${token}`

                // Set marker cookie so Next.js middleware can detect login
                document.cookie = `logged_in=1; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`
            } else {
                // --- Cookie-based auth (same-host client) ---
                await axios.post('/login', props)

                document.cookie = `logged_in=1; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`
            }

            setIsSigning(false)
            setStatus('Login successful! Redirecting...')
            let requiresSetup = !!mfaSetupRequired
            try {
                const setupStatus = await axios.get('/api/mfa/setup-status')
                requiresSetup =
                    requiresSetup || !!setupStatus?.data?.mfa_setup_required
            } catch {
                const refreshedUser = await mutate()
                requiresSetup =
                    requiresSetup ||
                    !!refreshedUser?.security?.mfa_setup_required
            }
            mutate().catch(() => {})
            router.replace(requiresSetup ? setupPath : '/')
        } catch (error) {
            setIsSigning(false)
            let message = 'Login failed. Please try again.'
            if (error.response) {
                if (
                    error.response.status === 428 &&
                    error.response.data?.mfa_required
                ) {
                    const methods = (error.response.data?.methods || []).join(
                        ', ',
                    )
                    message =
                        error.response.data?.message ||
                        (methods
                            ? `MFA required. Use: ${methods}`
                            : 'MFA required. Enter your authenticator code, recovery code, or passkey.')
                    return {
                        ok: false,
                        mfaRequired: true,
                        methods: error.response.data?.methods || [],
                        message,
                    }
                } else if (error.response.data && error.response.data.message) {
                    message = error.response.data.message
                } else if (typeof error.response.data === 'string') {
                    message = error.response.data
                }
            } else if (error.request) {
                message = 'Network error. Please check your connection.'
            } else if (error.message) {
                message = error.message
            }
            setErrors(message)
            setStatus(null)
            if (typeof window !== 'undefined' && window.console) {
                console.error('Login error:', error)
            }
            return {
                ok: false,
                mfaRequired: false,
                methods: [],
                message,
            }
        }

        return {
            ok: true,
            mfaRequired: false,
            methods: [],
            message: null,
        }
    }

    const tokenBasedPasskeyLogin = async ({ login, setErrors, setStatus }) => {
        setIsSigning(true)
        try {
            setErrors([])
            setStatus(null)

            const optionsResponse = await axios.post('/login-passkey/options', {
                login,
            })
            const publicKey = optionsResponse.data?.publicKey
            if (!publicKey) {
                throw new Error('Passkey options are missing.')
            }

            const credential = await navigator.credentials.get({
                publicKey: {
                    ...publicKey,
                    challenge: base64UrlToBuffer(publicKey.challenge),
                    allowCredentials: (publicKey.allowCredentials || []).map(
                        cred => ({
                            ...cred,
                            id: base64UrlToBuffer(cred.id),
                        }),
                    ),
                },
            })

            if (!credential) {
                throw new Error('Passkey authentication was cancelled.')
            }

            const assertionResponse = credential.response
            const loginResponse = await axios.post('/login-passkey', {
                login,
                id: credential.id,
                response: {
                    clientDataJSON: bufferToBase64Url(
                        assertionResponse.clientDataJSON,
                    ),
                    authenticatorData: bufferToBase64Url(
                        assertionResponse.authenticatorData,
                    ),
                    signature: bufferToBase64Url(assertionResponse.signature),
                },
            })

            const {
                token,
                roles: userRoles,
                permissions: userPermissions,
                mfa_setup_required: needsSetup,
                security,
            } = loginResponse.data

            localStorage.setItem('auth_token', token)
            localStorage.setItem('roles', JSON.stringify(userRoles))
            localStorage.setItem('permissions', JSON.stringify(userPermissions))
            if (security) {
                localStorage.setItem('security', JSON.stringify(security))
            }

            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
            document.cookie = `logged_in=1; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`

            setStatus('Login successful! Redirecting...')
            mutate().catch(() => {})
            router.replace(needsSetup ? setupPath : '/')
        } catch (error) {
            let message = 'Passkey login failed. Please try again.'
            if (error?.response?.data?.message) {
                message = error.response.data.message
            } else if (
                error?.response?.status === 401 ||
                error?.response?.status === 422
            ) {
                message = 'Invalid passkey. Please try again.'
            } else if (error?.message) {
                message = error.message
            }
            setErrors(message)
            setStatus(null)
        } finally {
            setIsSigning(false)
        }
    }

    const requestEmailOtp = async ({ email, setErrors, setStatus }) => {
        try {
            await csrf()
            setErrors([])
            setStatus(null)

            const response = await axios.post('/login-otp/request', {
                email,
            })

            setStatus(
                response.data?.message ||
                    'If the email exists, an OTP has been sent.',
            )
            return { ok: true }
        } catch (error) {
            let message = 'Unable to send OTP. Please try again.'
            if (error?.response?.data?.message) {
                message = error.response.data.message
            }

            setErrors(message)
            setStatus(null)
            return { ok: false, message }
        }
    }

    const loginWithEmailOtp = async ({ setErrors, setStatus, ...props }) => {
        setIsSigning(true)
        try {
            await csrf()
            setErrors([])
            setStatus(null)

            const response = await axios.post('/login-otp', props)

            const {
                token,
                roles: userRoles,
                permissions: userPermissions,
                mfa_setup_required: needsSetup,
                security,
            } = response.data

            if (isTokenMode) {
                localStorage.setItem('auth_token', token)
                localStorage.setItem('roles', JSON.stringify(userRoles))
                localStorage.setItem(
                    'permissions',
                    JSON.stringify(userPermissions),
                )
                if (security) {
                    localStorage.setItem('security', JSON.stringify(security))
                }

                axios.defaults.headers.common['Authorization'] =
                    `Bearer ${token}`
            }

            document.cookie = `logged_in=1; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`

            setStatus('Login successful! Redirecting...')
            mutate().catch(() => {})
            router.replace(needsSetup ? setupPath : '/')

            return {
                ok: true,
                mfaRequired: false,
                methods: [],
                message: null,
            }
        } catch (error) {
            let message = 'OTP login failed. Please try again.'

            if (error.response) {
                if (
                    error.response.status === 428 &&
                    error.response.data?.mfa_required
                ) {
                    const methods = (error.response.data?.methods || []).join(
                        ', ',
                    )
                    message =
                        error.response.data?.message ||
                        (methods
                            ? `MFA required. Use: ${methods}`
                            : 'MFA required. Enter your authenticator code, recovery code, or passkey.')

                    return {
                        ok: false,
                        mfaRequired: true,
                        methods: error.response.data?.methods || [],
                        message,
                    }
                }

                if (error.response.data?.message) {
                    message = error.response.data.message
                }
            }

            setErrors(message)
            setStatus(null)
            return {
                ok: false,
                mfaRequired: false,
                methods: [],
                message,
            }
        } finally {
            setIsSigning(false)
        }
    }

    const forgotPassword = async ({ setErrors, setStatus, email }) => {
        await csrf()

        setErrors([])
        setStatus(null)

        axios
            .post('/forgot-password', { email })
            .then(response => setStatus(response.data.status))
            .catch(error => {
                if (error.response.status !== 422) throw error

                setErrors(error.response.data.errors)
            })
    }

    const resetPassword = async ({ setErrors, setStatus, ...props }) => {
        // await csrf()
        mutate().catch(() => {})
        router.replace(needsSetup ? setupPath : '/')

        setErrors([])
        setStatus(null)

        axios
            .post('/reset-password', { token: params.token, ...props })
            .then(response =>
                router.push('/login?reset=' + btoa(response.data.status)),
            )
            .catch(error => {
                if (error.response.status !== 422) throw error

                setErrors(error.response.data.errors)
            })
    }

    const resendEmailVerification = ({ setStatus }) => {
        axios
            .post('/email/verification-notification')
            .then(response => setStatus(response.data.status))
    }

    const logout = async () => {
        // Fire server-side logout without blocking client redirect.
        // UI should transition to /login immediately after local auth state is cleared.
        const logoutRequest = axios
            .post(isTokenMode ? '/logout-token' : '/logout')
            .catch(() => {
                // ignore errors but continue clearing client state
            })

        clearClientAuthState()

        // Broadcast logout event so other tabs clear and redirect too.
        try {
            localStorage.setItem(
                AUTH_EVENT_KEY,
                JSON.stringify({ type: 'logout', at: Date.now() }),
            )
        } catch (e) {
            // ignore
        }

        // Optimistically clear user from SWR cache BEFORE navigating to /login.
        // Without this, the login page mounts with stale user data still in cache,
        // triggering redirectIfAuthenticated → /dashboard immediately after logout.
        mutate(null, false)

        router.replace('/login')

        await logoutRequest
    }

    const revalidateUser = () => mutate()

    return {
        tokenBasedlogin,
        tokenBasedPasskeyLogin,
        requestEmailOtp,
        loginWithEmailOtp,
        user,
        error,
        revalidateUser,
        register,
        login,
        forgotPassword,
        resetPassword,
        resendEmailVerification,
        logout,
        isSigning,
        isLoading,
        isValidating,
        roles,
        isAuthenticated: !!user?.id,
        permissions,
    }
}
